import {
    DocumentModel,
    DocumentPointModel,
    FieldGroupType,
    ITemplateBuilderTableFieldModel,
    TemplateBuilderCustomFieldModel,
    TemplateBuilderTableCellFieldModel,
    TemplateBuilderTableFieldModel,
    TemplateBuilderTableHeaderFieldModel,
} from "@api/web-api-client";
import SdConstants from "@src/configs/constants";
import hexRgb from "hex-rgb";
import { v4 as uuidv4 } from "uuid";
import { booleanOverlap } from "@turf/boolean-overlap";
import { booleanTouches } from "@turf/boolean-touches";
import { booleanContains } from "@turf/boolean-contains";
import { polygon } from "@turf/helpers";
import { ITemplateBuilderTableState } from "@src/redux/templateBuilder";

interface FieldIdAndBounds {
    points: DocumentPointModel[];
    uniqueId: string;
}

interface PointGroup {
    allPoints: FieldIdAndBounds[]; // Note: TableFields may have more than 1 set of point models. 1 per table cell
    fieldUniqueId: string;
}

export default class TemplateBuilderUtils {
    public static buildDefaultField(
        workspaceId: number,
        groupType?: FieldGroupType,
    ): TemplateBuilderCustomFieldModel {
        let newFillColor = TemplateBuilderUtils.generateRandomColor();
        const strokeColor = newFillColor;

        if (newFillColor.startsWith("#")) {
            const colorAsRgb = hexRgb(newFillColor);
            newFillColor = `rgba(${colorAsRgb.red}, ${colorAsRgb.green}, ${colorAsRgb.blue}, 0.32)`;
        }

        const model = new TemplateBuilderCustomFieldModel();

        model.init({
            categoryKey: "",
            displayName: "",
            description: "",
            fillColor: newFillColor,
            strokeColor: strokeColor,
            groupType: groupType,
            workspaceId: workspaceId,
        });

        return model;
    }

    public static getDotStyles(fillColor: string, strokeColor: string) {
        return {
            backgroundColor: fillColor,
            borderColor: strokeColor,
        };
    }

    private static createTurfPolygon(featPoints: DocumentPointModel[]) {
        const pointsAsNumbers = featPoints.map((model) => [model.x, model.y]);
        const polygonDefinitionList = [...pointsAsNumbers, pointsAsNumbers[0]];

        return polygon([polygonDefinitionList]);
    }

    public static getTemplateBuilderFieldsIntersections(
        documentModels: DocumentModel[],
        templateBuilderFields: TemplateBuilderCustomFieldModel[],
        tables: ITemplateBuilderTableState[],
    ): boolean {
        for (const documentModel of documentModels) {
            const pointGroupsInFeats: PointGroup[] = documentModel.entities
                .filter(
                    (entityModel) =>
                        entityModel.templateBuilderField?.type !==
                            SdConstants.TemplateBuilder
                                .SdKnownTemplateFieldTypes.Table &&
                        !!entityModel.templateBuilderField?.points &&
                        entityModel.templateBuilderField?.keyValues.length >
                            0 &&
                        templateBuilderFields.some(
                            (field) =>
                                field.uniqueId ===
                                entityModel.templateBuilderField?.fieldUniqueId,
                        ),
                )
                .map((entityModel) => {
                    return {
                        allPoints: [
                            {
                                points: entityModel.templateBuilderField!
                                    .points!,
                                uniqueId:
                                    entityModel.templateBuilderField
                                        ?.fieldUniqueId,
                            } as FieldIdAndBounds,
                        ],
                        fieldUniqueId:
                            entityModel.templateBuilderField?.fieldUniqueId,
                    } as PointGroup;
                });

            const pointGroupsInTables: PointGroup[] = tables
                .filter(
                    (tf) =>
                        !!tf.table &&
                        (tf.table?.rowCount ?? 0) > 0 &&
                        documentModel.entities.some(
                            (e) =>
                                e.templateBuilderField?.fieldUniqueId ===
                                tf.fieldUniqueId,
                        ),
                )
                .map((table) => {
                    const allTablePointSets = Object.values(
                        table.table!.tableRows,
                    ).flatMap((row) =>
                        row
                            .filter((cell) => cell.points !== undefined)
                            .map(
                                (cell) =>
                                    ({
                                        uniqueId: cell.uniqueId,
                                        points: cell.points,
                                    }) as FieldIdAndBounds,
                            ),
                    );

                    return {
                        allPoints: allTablePointSets,
                        fieldUniqueId: table.fieldUniqueId,
                    } as PointGroup;
                });

            const allPointsGroups =
                pointGroupsInFeats.concat(pointGroupsInTables);

            for (let i = 0; i < allPointsGroups.length - 1; i++) {
                const pointGroup1 = allPointsGroups[i];

                for (const p1 of pointGroup1.allPoints) {
                    const polygon1 = TemplateBuilderUtils.createTurfPolygon(
                        p1.points,
                    );

                    for (let j = 1; j < allPointsGroups.length; j++) {
                        const pointGroup2 = allPointsGroups[j];
                        if (
                            pointGroup1.fieldUniqueId !==
                            pointGroup2.fieldUniqueId
                        ) {
                            for (const p2 of pointGroup2.allPoints) {
                                const polygon2 =
                                    TemplateBuilderUtils.createTurfPolygon(
                                        p2.points,
                                    );

                                const overlaps = booleanOverlap(
                                    polygon1,
                                    polygon2,
                                );

                                if (overlaps) {
                                    const touches = booleanTouches(
                                        polygon1,
                                        polygon2,
                                    );

                                    if (!touches) {
                                        return true;
                                    }
                                }

                                const contains = booleanContains(
                                    polygon1,
                                    polygon2,
                                );
                                if (contains) {
                                    return true;
                                }

                                const isContained = booleanContains(
                                    polygon2,
                                    polygon1,
                                );
                                if (isContained) {
                                    return true;
                                }
                            }
                        }
                    }
                }
            }
        }
        return false;
    }

    private static generateRandomColor() {
        // Generate a random integer up to the maximum hexadecimal value possible
        const randomNumber = Math.floor(Math.random() * 0xffffff);

        // Convert it to a string with hexadecimal radix
        const randomNumberStr = randomNumber.toString(16);

        // Return the color padded with zeros
        return `#${randomNumberStr.padStart(6, "0").toUpperCase()}`;
    }

    public static createInitialTable = (
        headers: TemplateBuilderTableHeaderFieldModel[],
    ): ITemplateBuilderTableFieldModel => {
        const initialTable = new TemplateBuilderTableFieldModel();
        initialTable.init({
            rowCount: 1,
            columnCount: headers.length + 1,
            tableHeaders: [],
            tableRows: {},
        });

        initialTable.tableHeaders = [...headers];

        initialTable.tableRows["1"] = headers.map((header) => {
            const newCell = new TemplateBuilderTableCellFieldModel();
            newCell.init({
                uniqueId: uuidv4(),
                headerId: header.uniqueId,
                rowIndex: 1,
                columnIndex: header.columnIndex,
            });

            return newCell;
        });

        return initialTable;
    };

    public static generateExistingCategoriesSet(
        fields: TemplateBuilderCustomFieldModel[],
        currentCategoryKey?: string,
    ): Set<string> {
        const temporaryFields: string[] = [];

        for (const field of fields) {
            if (field.table) {
                for (const header of field.table.tableHeaders) {
                    temporaryFields.push(header.category);
                }
            }

            temporaryFields.push(field.categoryKey);
        }

        return new Set<string>(
            temporaryFields.filter((o) => o !== currentCategoryKey),
        );
    }

    public static generateUniqueTableHeaderCategory(
        existingCategories: Set<string>,
        t: (key: string, args: any) => string,
        overrideHeaderName?: string,
    ): string {
        // If an override was already provided and it doesn't exist yet, no need to generate a new one
        if (overrideHeaderName && !existingCategories.has(overrideHeaderName)) {
            return overrideHeaderName;
        }

        let index = 0;
        let category = "";

        do {
            index++;

            category = overrideHeaderName
                ? `${overrideHeaderName}${index}`
                : t("Task.TemplateBuilder.Tables.AutoLabel.Modal.Column", {
                      colNumber: index,
                  });
        } while (existingCategories.has(category));

        return category;
    }

    public static checkIfTableHeaderCategoryIsUnique(
        existingCategories: Set<string>,
        tableHeaders: TemplateBuilderTableHeaderFieldModel[],
        value: string,
        currentColumnIndex: number,
    ) {
        const existingCategoriesPlusHeaders = new Set<string>([
            ...existingCategories,
            ...tableHeaders.map((th) => th.category),
        ]);

        const header = tableHeaders.find((th) => th.category === value);

        // If a header was found with the same category, remove it from the existing categories set to avoid checking against itself
        if (header && header.category === value) {
            existingCategoriesPlusHeaders.delete(value);
        }

        const categoryExists = existingCategoriesPlusHeaders.has(value);

        const result =
            (!header || header.columnIndex === currentColumnIndex) &&
            !categoryExists;

        return result;
    }
}
